import { useEffect, useState } from "react";
import { Check, MapPin } from "lucide-react";
import { fetchApi, reasonOf } from "./access";

/**
 * A location tag that Instagram will accept: search a place, pick it (item 109).
 * Typing alone tags nothing — Meta needs the place's id — so the field says so
 * until a place is picked. Search is GET /api/instagram/locations, which needs
 * the "Page Public Metadata Access" permission on Olli's Meta app; without it
 * Meta's own message is shown. An account that isn't connected answers 500
 * "Integration not found", which reads as "connect Instagram".
 */

export function IGLocationField({ integrationId, value, locationId, onChange, inputClass }) {
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(""); // the text the current results answer
  useEffect(() => {
    if (!integrationId || locationId || (value || "").trim().length < 2) { setResults([]); return undefined; }
    // A late answer for an older search is dropped.
    let live = true;
    const id = setTimeout(() => {
      fetchApi("/api/instagram/locations", { params: { integrationId, q: value.trim() } }).then((r) => {
        if (!live) return;
        if (r.ok) { setResults(r.data?.data || []); setError(""); setSearched(value.trim()); return; }
        setResults([]);
        setError(reasonOf(r)?.kind === "connect" ? "Connect Instagram to tag a place." : r.message || "Location search isn't available right now.");
      });
    }, 300);
    return () => { live = false; clearTimeout(id); };
  }, [integrationId, value, locationId]);

  return <span className="relative block">
    <MapPin size={15} strokeWidth={1.75} className="absolute left-3 top-3 text-muted" />
    <input value={value || ""} onChange={(e) => onChange({ location: e.target.value, locationId: null })} placeholder={integrationId ? "Search a place" : "Connect Instagram to tag a place"} disabled={!integrationId} className={`${inputClass} pl-9 disabled:opacity-60`} />
    {!locationId && results.length > 0 && <span className="absolute inset-x-0 top-full z-20 mt-1 block max-h-56 overflow-auto rounded-sm bg-paper p-1 shadow-lift ring-1 ring-line">{results.map((p) => <button key={p.id} type="button" onClick={() => { onChange({ location: p.name, locationId: p.id }); setResults([]); }} className="block w-full rounded-xs px-3 py-2 text-left text-small hover:bg-mist">{p.name}{p.city ? <span className="ml-2 text-caption text-muted">{p.city}</span> : null}</button>)}</span>}
    {locationId ? <span className="mt-1 flex items-center gap-1 text-caption text-success"><Check size={12} />Tagged on the post</span>
      : error ? <span className="mt-1 block text-caption text-warning">{error}</span>
        : (value || "").trim() && searched === (value || "").trim() && !results.length ? <span className="mt-1 block text-caption text-muted">No places found. Try another name.</span>
          : (value || "").trim() ? <span className="mt-1 block text-caption text-muted">Pick a place from the list to tag it.</span> : null}
  </span>;
}
